import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for DiePress module to handle 401 errors
const diePressAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for DiePress module
diePressAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// DiePress APIs
const getDiePressList = (page, pagesize, search, ordering) => {
  return diePressAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/die-press/', {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDiePressById = id => {
  return diePressAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-press/${id}/`, {
    headers: authHeader(),
  });
};

const addDiePress = formData => {
  return diePressAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-press/`,
    formData,
    { headers: authHeader() },
  );
};

const updateDiePress = (id, formPayload) => {
  return diePressAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/die-press/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchDiePress = (id, formPayload) => {
  return diePressAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/die-press/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const deleteDiePress = id => {
  return diePressAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/die-press/${id}/`,
    { headers: authHeader() },
  );
};

const getDiePressDropdown = (filterKey) => {
  return diePressAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-press/dropdown/`, {
    headers: authHeader(),
    params: {
      ...(filterKey && { filter_key: filterKey }),
    },
  });
};

const getDiePressArchiveList = (page, pagesize, search, ordering) => {
  return diePressAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-press/archived/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getDiePressArchiveById = id => {
  return diePressAxiosInstance.get(`${API_URL}/api/${API_VERSION}/die-press-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreDiePress = ids => {
  return diePressAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-press/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveDiePress = ids => {
  return diePressAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-press/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportDiePress = formData => {
  return diePressAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/die-press/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return diePressAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-press/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return diePressAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-press/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return diePressAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/die-press/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const diePressService = {
  getDiePressList,
  getDiePressById,
  addDiePress,
  updateDiePress,
  patchDiePress,
  deleteDiePress,
  getDiePressDropdown,
  getDiePressArchiveList,
  getDiePressArchiveById,
  restoreDiePress,
  bulkArchiveDiePress,
  bulkImportDiePress,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default diePressService;
